import {
  EnvelopeIcon,
  GlobeAltIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { ComponentType, ReactNode } from 'react';

import { ReadDepartmentRecordsChannel } from '@/services/api/generated/policeDepartmentsService';

// ============= ICON & TYPE LOGIC =============

export const getChannelIcon = (
  channel: ReadDepartmentRecordsChannel,
): ComponentType<{ className?: string }> => {
  const handleType = channel.handle?.handle_type?.toUpperCase();
  const majorVendor = channel.major_vendor;

  if (
    majorVendor &&
    majorVendor !== 'not_a_portal' &&
    majorVendor !== 'independent_portal'
  ) {
    return GlobeAltIcon;
  }

  switch (handleType) {
    case 'WEBSITE':
      return GlobeAltIcon;
    case 'PHONE':
    case 'FAX':
      return PhoneIcon;
    case 'EMAIL':
    case 'MAILING_ADDRESS':
      return EnvelopeIcon;
    default:
      return GlobeAltIcon;
  }
};

export const getChannelTypeLabel = (
  channel: ReadDepartmentRecordsChannel,
): string => {
  const majorVendor = channel.major_vendor;

  if (majorVendor === 'carfax_crashdocs') return 'CrashDocs';
  if (majorVendor === 'lexis_nexis_buycrash') return 'BuyCrash';
  if (majorVendor === 'txdot_cris') return 'TxDOT CRIS';
  if (majorVendor === 'independent_portal') return 'Online Portal';

  const handleType = channel.handle?.handle_type?.toUpperCase();
  switch (handleType) {
    case 'WEBSITE':
      return 'Website';
    case 'PHONE':
      return 'Phone';
    case 'EMAIL':
      return 'Email';
    case 'MAILING_ADDRESS':
      return 'Mail';
    case 'FAX':
      return 'Fax';
    default:
      return 'Other';
  }
};

// ============= DISPLAY COMPONENTS =============

interface DataDisplayProps {
  value: ReactNode;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Display component with proper hierarchy: VALUE is prominent, label is secondary
 */
export const DataDisplay = ({
  value,
  label,
  size = 'md',
  className = '',
}: DataDisplayProps) => {
  const sizeClasses = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-2xl',
  };

  return (
    <div className={className}>
      {label && (
        <div className="mb-0.5 text-xs font-medium uppercase tracking-wide text-gray-500">
          {label}
        </div>
      )}
      <div className={`font-semibold text-gray-900 ${sizeClasses[size]}`}>
        {value}
      </div>
    </div>
  );
};

interface InfoCardProps {
  children: ReactNode;
  variant?: 'default' | 'warning';
  className?: string;
}

/**
 * Consistent card styling for info sections
 */
export const InfoCard = ({
  children,
  variant = 'default',
  className = '',
}: InfoCardProps) => {
  const variantClasses = {
    default: 'border-gray-200 bg-gray-50',
    warning: 'border-amber-300 bg-amber-50',
  };

  return (
    <div
      className={`rounded-lg border p-4 ${variantClasses[variant]} ${className}`}
    >
      {children}
    </div>
  );
};

interface IconBadgeProps {
  icon: ComponentType<{ className?: string }>;
  label: string;
  className?: string;
}

/**
 * Icon + label badge
 */
export const IconBadge = ({
  icon: Icon,
  label,
  className = '',
}: IconBadgeProps) => {
  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <Icon className="h-5 w-5 text-gray-400" />
      <span className="text-sm font-medium text-gray-600">{label}</span>
    </div>
  );
};

interface SectionHeaderProps {
  children: ReactNode;
}

/**
 * Consistent section header styling
 */
export const SectionHeader = ({ children }: SectionHeaderProps) => {
  return (
    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-700">
      {children}
    </h4>
  );
};

interface DataGridProps {
  columns?: number;
  children: ReactNode;
  className?: string;
}

/**
 * Responsive grid for data items
 */
export const DataGrid = ({
  columns = 2,
  children,
  className = '',
}: DataGridProps) => {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
  };

  return (
    <div
      className={`grid gap-4 ${colClasses[columns as keyof typeof colClasses] || colClasses[2]} ${className}`}
    >
      {children}
    </div>
  );
};

interface MetricProps {
  value: string | number;
  suffix?: string;
  label: string;
}

/**
 * Display a metric with large value and small label
 */
export const Metric = ({ value, suffix, label }: MetricProps) => {
  return (
    <div>
      <div className="text-2xl font-bold text-gray-900">
        {value}
        {suffix && <span className="ml-1 text-lg text-gray-500">{suffix}</span>}
      </div>
      <div className="mt-0.5 text-xs text-gray-600">{label}</div>
    </div>
  );
};

interface ExternalLinkProps {
  href: string;
  children: ReactNode;
}

/**
 * Consistent external link styling
 */
export const ExternalLink = ({ href, children }: ExternalLinkProps) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
    >
      {children}
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
        />
      </svg>
    </a>
  );
};
